import {
  BaseFullSchema_OLD,
  BaseRefSchema,
  BaseCreateSchema,
  BaseUpdateSchema,
  PaginatedResponse,
  IdAsUUID,
} from "./common/commonTypes";
import {
  PaymentTermType,
  CustomerShipmentBillingType,
} from "./common/termTypes";
import {
  ContactCreate,
  ContactUpdate,
  ContactRef,
  EmployeeRef,
} from "./personTypes";
import { AddressCreate, AddressUpdate, AddressRef } from "./addressTypes";
import {
  TableDefinition,
  BaseModelFieldKeys,
  BaseModelFields,
} from "./common/tableDefinitionTypes";
import {} from "./common/baseTableTypes";

declare module "./common/baseTableTypes" {
  interface TableDefinitionRegistry {
    customers: typeof CustomerTableDefinition;
    vendors: typeof VendorTableDefinition;
  }
}

export type CompanyType = "vendor" | "customer";

export interface CompanyFullBase extends BaseFullSchema_OLD {
  name: string;
  legal_name: string;
  company_type: CompanyType;
  notes: string | null;
  code: string;
  default_payment_terms: PaymentTermType;
  credit_limit: string;
  primary_shipping_addr_id?: IdAsUUID;
  primary_billing_addr_id?: IdAsUUID;
  contacts: ContactRef[];
  addresses: AddressRef[];
  centese_contact?: EmployeeRef;
  preferred_shipping_provider?: string;
  preferred_shipping_methods?: { fedex: string | null; ups: string | null };
  box_folder_id?: string;
}

export interface CompanyRefBase extends BaseRefSchema {
  name: string;
  company_type: CompanyType;
  notes?: string;
  code: string;
}

export interface CompanyCreateBase extends BaseCreateSchema {
  name: string;
  legal_name: string;
  company_type: CompanyType;
  notes: string | null;
  code: string | null;
  default_payment_terms: PaymentTermType;
  credit_limit: number;
  new_contacts: ContactCreate[];
  new_addresses: AddressCreate[];
  preferred_shipping_provider: string | null;
  preferred_shipping_methods: {
    fedex: string | null;
    ups: string | null;
  } | null;
  box_folder_id: string | null;
  centese_contact_id: IdAsUUID | null;
}

export interface CompanyUpdateBase extends BaseUpdateSchema {
  name?: string;
  company_type: CompanyType;
  notes?: string | null;
  code?: string;
  default_payment_terms?: PaymentTermType;
  credit_limit?: number;
  primary_shipping_addr_id?: IdAsUUID;
  primary_billing_addr_id?: IdAsUUID;
  new_contacts: ContactCreate[];
  updated_contacts: ContactUpdate[];
  deleted_contacts: IdAsUUID[];
  new_addresses: AddressCreate[];
  updated_addresses: AddressUpdate[];
  deleted_addresses: IdAsUUID[];
  preferred_shipping_provider?: string;
  preferred_shipping_methods?: {
    fedex: string | null | undefined;
    ups: string | null | undefined;
  };
  box_folder_id?: string;
  centese_contact_id?: IdAsUUID;
}

// Vendor Types
export interface VendorFull extends CompanyFullBase {
  company_type: "vendor";
}

export interface VendorRef extends CompanyRefBase {
  company_type: "vendor";
}

export interface VendorCreate extends CompanyCreateBase {
  company_type: "vendor";
}

export interface VendorUpdate extends CompanyUpdateBase {
  company_type: "vendor";
}

export const CUSTOMER_TYPES = ["CARDIAC", "THORACIC", "BOTH"] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];

export interface CustomerFull extends CompanyFullBase {
  company_type: "customer";
  shipping_accounts: { fedex: string | null; ups: string | null };
  customer_type: CustomerType;
  shipment_billing_preference: CustomerShipmentBillingType;
}

export interface CustomerRef extends CompanyRefBase {
  company_type: "customer";
}

export interface CustomerCreate extends CompanyCreateBase {
  company_type: "customer";
  shipping_accounts: { fedex: string | null; ups: string | null };
  customer_type: CustomerType;
  shipment_billing_preference: CustomerShipmentBillingType;
}

export interface CustomerUpdate extends CompanyUpdateBase {
  company_type: "customer";
  shipping_accounts?: {
    fedex: string | null | undefined;
    ups: string | null | undefined;
  };
  customer_type?: CustomerType;
  shipment_billing_preference?: CustomerShipmentBillingType;
}

export type PaginatedVendorResponse = PaginatedResponse<VendorFull>;
export type PaginatedCustomerResponse = PaginatedResponse<CustomerFull>;

export type CompanyFullGroup = CustomerFull | VendorFull;
export type CompanyRefGroup = CustomerRef | VendorRef;
export type CompanyUpdateGroup = CustomerUpdate | VendorUpdate;
export type CompanyCreateGroup = CustomerCreate | VendorCreate;
export type PaginatedCompanyResponseGroup =
  | PaginatedVendorResponse
  | PaginatedCustomerResponse;

const CustomerTableDefinition = {
  friendlyName: "Customers",
  fields: {},
} as const satisfies TableDefinition;

const VendorTableDefinition = {
  friendlyName: "Vendors",
  fields: {},
} as const satisfies TableDefinition;
